<script>
import { defineComponent, ref, onMounted, watch } from 'vue'
import { useAuthStore } from '@/stores/user'
import { useRouter, useRoute } from 'vue-router'
import axiosInstance from '@/axios/axios'
import { CheckIcon } from '@heroicons/vue/24/outline'
import DrawingPackages from '@/components/DrawingPackages.vue'

export default defineComponent({
  name: 'PaymentPage',

  components: {
    CheckIcon,
    DrawingPackages
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    return { router, route }
  },

  data() {
    return {
      packages: [],
      isLoading: false,
      userInfo: {},
      isDebugVisible: false,
      navigator: window.navigator
    }
  },

  computed: {
    userStore() {
      return useAuthStore()
    }
  },

  methods: {
    async fetchUserInfo() {
      try {
        const response = await axiosInstance.get('/api/auth/userinfo')
        this.userInfo = response.data
        this.userStore.setUser(response.data)
      } catch (error) {
        console.error('Error fetching user info:', error)
      }
    },

    async fetchPackages() {
      try {
        this.isLoading = true
        const response = await axiosInstance.get('/draw/api/v1/free/packages/')
        this.packages = response.data
      } catch (error) {
        console.error('Error fetching packages:', error)
      } finally {
        this.isLoading = false
      }
    }
  },

  mounted() {
    this.fetchPackages()
    this.fetchUserInfo()
  }
})
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-50 py-12">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-16">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">
          {{ $t('message.unlockAllFeatures') }} ✨
        </h1>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          {{ userInfo.mandala_drawings ?
            `${$t('message.remainingMandala')}: ${userInfo.mandala_drawings.remaining}` :
            $t('message.limitedFeatures')
          }}
        </p>
      </div>

      <div v-if="isLoading" class="flex justify-center my-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
      
      <DrawingPackages v-else :packages="packages" />

      <!-- Trust Indicators -->
      <div class="text-center space-y-4 mt-16">
        <div class="flex flex-wrap justify-center gap-8">
          <div class="flex items-center">
            <span class="text-2xl mr-2">🔒</span>
            <span class="text-gray-600">Безопасная оплата</span>
          </div>
          <div class="flex items-center">
            <span class="text-2xl mr-2">⚡️</span>
            <span class="text-gray-600">Мгновенный доступ</span>
          </div>
          <div class="flex items-center">
            <span class="text-2xl mr-2">💫</span>
            <span class="text-gray-600">Начните меняться прямо сейчас</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="route.query.redirect" class="text-center mt-8">
        <button @click="router.push(route.query.redirect)" class="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
            Continue to Application
        </button>
        <p class="text-xs text-gray-500 mt-2">Use this if you have already paid but are stuck here.</p>
    </div>

    <!-- Debug Info -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 pb-12">
      <button @click="isDebugVisible = !isDebugVisible" 
              class="text-sm text-gray-400 hover:text-gray-600 underline mb-4">
        {{ isDebugVisible ? 'Hide Debug Info' : 'Show Debug Info' }}
      </button>
      
      <div v-if="isDebugVisible" class="bg-gray-100 p-4 rounded-lg font-mono text-xs overflow-auto">
        <h3 class="font-bold mb-2">Debug Information</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="font-bold">Route Query:</p>
            <pre>{{ JSON.stringify(route.query, null, 2) }}</pre>
            
            <p class="font-bold mt-2">Subscription Status:</p>
            <pre>{{ JSON.stringify(userInfo.subscription_status, null, 2) }}</pre>

            <p class="font-bold mt-2">Drawings Balance:</p>
            <pre>{{ JSON.stringify(userInfo.mandala_drawings, null, 2) }}</pre>
          </div>
          <div>
            <p class="font-bold mt-2">User Agent:</p>
            <pre>{{ navigator.userAgent }}</pre>
            
            <p class="font-bold mt-2">Timestamp:</p>
            <pre>{{ new Date().toISOString() }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
